import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Images } from "lucide-react";
import { Img } from "@/components/shared/Img";
import { Lightbox } from "@/components/shared/Lightbox";
import { PhotoCredit } from "@/components/shared/PhotoCredit";
import { cn } from "@/lib/utils";
import { explicitFit } from "@/lib/display";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";

const MAX_TILES = 8;

/** Mobile: two-column mosaic, first photo full width, no orphan tile. Desktop (sm+): asymmetric bento per photo count. */
export const tileClass = (n, i, count) => {
  const mobile = i === 0 ? "col-span-2 aspect-[16/10]" : i === count - 1 && (count - 1) % 2 === 1 ? "col-span-2 aspect-[2/1]" : "aspect-[4/3]";
  let desk;
  if (n === 2) desk = i === 0 ? "sm:col-span-3 sm:aspect-[4/3]" : "sm:col-span-2 sm:aspect-[4/3]";
  else if (n === 3) desk = i === 0 ? "sm:col-span-2 sm:row-span-2 sm:aspect-auto" : "sm:col-span-1 sm:aspect-[4/3]";
  else if (n === 4) desk = i === 0 ? "sm:col-span-2 sm:row-span-2 sm:aspect-auto" : i === 3 ? "sm:col-span-2 sm:aspect-[2/1]" : "sm:col-span-1 sm:aspect-[4/3]";
  else if (i === 0) desk = "sm:col-span-2 sm:row-span-2 sm:aspect-auto";
  else if (i === 1) desk = "sm:col-span-1 sm:row-span-2 sm:aspect-auto";
  else if (i < 4) desk = "sm:col-span-1 sm:aspect-[4/3]";
  else {
    const rem = count - 4;
    desk = rem === 1 ? "sm:col-span-4 sm:aspect-[21/9]" : rem === 2 || (rem === 3 && i === 4) ? "sm:col-span-2 sm:aspect-[2/1]" : "sm:col-span-1 sm:aspect-[4/3]";
  }
  return `${mobile} ${desk}`;
};
const gridClass = (n) => (n === 2 ? "sm:grid-cols-5" : n === 3 ? "sm:grid-cols-3" : "sm:grid-cols-4");
const sizesFor = (n, i) => (i === 0 || n === 2 ? "(min-width: 1440px) 700px, (min-width: 640px) 50vw, 100vw" : "(min-width: 1440px) 340px, (min-width: 640px) 25vw, 50vw");

/**
 * Every photo of a block laid out as an asymmetric mosaic, each with its words on the picture, and a full-screen
 * viewer on click. One photo is a single captioned panorama. Works on paper (light) and ink (dark) grounds.
 */
export const PhotoMosaic = ({ images = [], captions = {}, display, alt = "", tone = "light", radius = "rounded-[12px]", max = MAX_TILES, onOpen, className, testId = "photo-mosaic" }) => {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(null);
  const n = images.length;
  if (!n) return null;
  const tiles = n > max ? images.slice(0, max) : images;
  const hidden = n - tiles.length;
  const openAt = onOpen || setOpen;
  const container = { hidden: {}, show: { transition: { staggerChildren: reduce ? 0 : 0.06 } } };
  const item = { hidden: { opacity: 0, y: reduce ? 0 : 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } } };
  const ring = tone === "dark" ? "bg-paper/5 ring-1 ring-paper/10 focus-visible:ring-sol" : "bg-secondary focus-visible:ring-foreground focus-visible:ring-offset-2";
  return (
    <>
      <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-10% 0px" }} className={cn("grid grid-cols-2 gap-3 sm:gap-4", n === 1 ? "sm:grid-cols-1" : gridClass(n), className)} data-testid={testId} data-count={n}>
        {tiles.map((src, i) => {
          const last = i === tiles.length - 1 && hidden > 0;
          const cap = captions[src];
          const ex = explicitFit(display, src);
          return (
            <motion.button key={`${src}-${i}`} variants={item} type="button" onClick={() => openAt(i)} aria-label={last ? `Vis alle ${n} bilete` : cap || `Bilete ${i + 1} av ${n}`} data-testid={`${testId}-item-${i}`}
              className={cn("group relative block w-full overflow-hidden transition-[transform,box-shadow] duration-500 hover:-translate-y-0.5 hover:shadow-[0_24px_60px_-28px_rgba(0,0,0,.6)] focus-visible:outline-none focus-visible:ring-2", radius, ring, n === 1 ? "col-span-2 aspect-[16/10] sm:col-span-1 sm:aspect-[21/9]" : tileClass(n, i, tiles.length))}>
              <Img src={src} alt={cap || (i === 0 ? alt : "")} width={i === 0 ? 1200 : 800} sizes={sizesFor(n, i)} style={ex.style} data-fit={ex.fit}
                className={cn("absolute inset-0 h-full w-full transition-transform duration-700 ease-out", ex.fit === "contain" ? cn("object-contain", cap && !last && "pb-10 sm:pb-11") : "object-cover group-hover:scale-[1.04]")} />
              {cap && !last && ex.fit === "contain" && (
                <span className="pointer-events-none absolute inset-x-0 bottom-0 flex h-10 items-center px-4 text-left sm:h-11 sm:px-5" data-testid={`${testId}-caption-${i}`}>
                  <span className={cn("block truncate text-[13px] font-medium leading-snug sm:text-[14px]", tone === "dark" ? "text-paper" : "text-foreground")}>{cap}</span>
                  <PhotoCredit src={src} className={cn("ml-3 shrink-0", tone === "dark" ? "!text-paper/70" : undefined)} testId={`${testId}-credit-${i}`} />
                </span>
              )}
              {cap && !last && ex.fit !== "contain" && (
                <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 via-ink/40 to-transparent px-4 pb-3 pt-12 text-left sm:px-5 sm:pb-4" data-testid={`${testId}-caption-${i}`}>
                  <span className={cn("block font-display font-medium leading-snug tracking-[-0.01em] text-paper [text-shadow:0_1px_12px_rgba(0,0,0,.45)]", i === 0 || n <= 2 ? "text-[15px] sm:text-[18px]" : "text-[13px] sm:text-[14px]")}>{cap}</span>
                  <PhotoCredit src={src} className="mt-1 !text-paper/70" testId={`${testId}-credit-${i}`} />
                </span>
              )}
              {!cap && !last && <PhotoCredit src={src} overlay testId={`${testId}-credit-${i}`} />}
              {!last && <PhotoNumberTag src={src} className="absolute right-2 top-2" testId={`${testId}-number-${i}`} />}
              {last && (
                <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-ink/65 text-paper backdrop-blur-sm transition-colors duration-300 group-hover:bg-ink/55" data-testid={`${testId}-more`}>
                  <span className="font-display text-[40px] font-semibold leading-none tracking-tight sm:text-[48px]">+{hidden}</span>
                  <span className="inline-flex items-center gap-1.5 text-[13px] text-paper/80"><Images className="h-3.5 w-3.5" /> fleire bilete</span>
                </span>
              )}
            </motion.button>
          );
        })}
      </motion.div>
      {!onOpen && <Lightbox images={images} index={open} onChange={setOpen} alt={alt} captions={captions} testId={testId} />}
    </>
  );
};
